import { useEffect, useRef } from "react";
import { useAuth } from "@/src/auth/hooks/use-auth";
import { supabase } from "@/src/common/services/supabase.service";

/**
 * Records one activity row per user per day (fuels the Daily Active Users
 * chart). Sign-ins are captured by a DB trigger, but persistent sessions can
 * go days without re-authenticating — this ping covers those. Re-checks when
 * the tab becomes visible so long-lived tabs still register a new day.
 */
export function useActivityPing() {
	const { profile } = useAuth();
	const lastPingDay = useRef<string | null>(null);
	const profileId = profile?.id ?? null;

	useEffect(() => {
		if (!profileId) return;
		const ping = () => {
			const today = new Date().toDateString();
			if (lastPingDay.current === today) return;
			lastPingDay.current = today;
			supabase.rpc("record_user_activity").then(({ error }) => {
				if (error) lastPingDay.current = null;
			});
		};
		ping();
		const onVisibilityChange = () => {
			if (document.visibilityState === "visible") ping();
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, [profileId]);
}
